import React, { useCallback, useEffect, useMemo, useState } from 'react';
import * as yup from 'yup';
import {
  Button,
  Col,
  Divider,
  Form,
  Input,
  InputNumber,
  Modal,
  notification,
  Row,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
} from 'antd';
import { useTranslation } from 'react-i18next';
import { SearchOutlined } from '@ant-design/icons';

import moment from 'moment/moment';
import useForm from '../../hooks/useForm';
import FormWrapper from '../FormWrapper';
import CancelButton from '../buttons/CancelButton';
import { getMobileTitlesByPhysicalId } from '../../services/accounts/transport-titles';
import { getMobilePassProducts } from '../../services/offers/products';
import { getAxisWithAdminUnits } from '../../services/taxonomies/zonal-restriction';
import { getRouteGroups } from '../../services/taxonomies/route-groups';
import useDictionaries from '../../hooks/useDictionaries';
import Div from '../pos2/Div';
import POSButton from '../pos2/POSButton';
import DatePickerLocale from '../DatePickerLocale';
import Restriction from '../orders/Restriction';
import ZonalRestriction from '../orders/ZonalRestriction';

const dictionaries = {
  routeGroups: getRouteGroups,
};

const renderTags = (val, colorMap) => {
  if (!val) return '-';
  const ids = String(val)
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  if (!ids.length) return '-';
  return (
    <span style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
      {ids.map((id) => (
        <Tag key={id} color={colorMap[id]?.color ?? 'default'}>
          {colorMap[id]?.name || id}
        </Tag>
      ))}
    </span>
  );
};

const badge = (
  value,
  trueLabel,
  falseLabel,
  trueBg,
  falseBg,
  trueText,
  falseText,
) => (
  <span
    style={{
      padding: '2px 8px',
      borderRadius: 6,
      background: value ? trueBg : falseBg,
      color: value ? trueText : falseText,
      fontWeight: 500,
    }}
  >
    {value ? trueLabel : falseLabel}
  </span>
);

const SuportMobileOrderForm = ({ data, onSubmit, onCancel }) => {
  const { t } = useTranslation();

  // restrictions
  const [{ routeGroups }] = useDictionaries(dictionaries);
  const [axis, setAxis] = useState([]);
  const [uats, setUats] = useState({});

  const [userInfo, setUserInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);
  const [isConfigured, setIsConfigured] = useState(false);
  const [titles, setTitles] = useState([]);
  const [passProducts, setPassProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [configForm] = Form.useForm();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isConfirmModalVisible, setIsConfirmModalVisible] = useState(false);

  const metropolitanEnabled = useMemo(
    () => window._env_.METROPOLITAN_ENABLE === 'true',
    [],
  );

  const validationSchema = useMemo(
    () =>
      yup.object().shape({
        physicalId: yup.string().required(t('errors.notEmpty')),
        accountId: yup.string().required(t('errors.notEmpty')),
      }),
    [t],
  );

  const form = useForm({
    initialValue: data,
    schema: validationSchema,
    validateOnChange: true,
    onSubmit,
    onCancel,
  });

  const { handleSubmit, input, setValue, isSubmitted } = form;

  useEffect(() => {
    getAxisWithAdminUnits().then((response) => {
      const adminUnits = Array.from(
        new Map(
          response
            .flatMap((axisList) => axisList.administrativeUnits ?? [])
            .map((unit) => [unit.id, unit]),
        ).values(),
      );
      setUats(
        adminUnits.reduce((acc, unit) => ({ ...acc, [unit.id]: unit }), {}),
      );
      setAxis(response);
    });
  }, []);

  useEffect(() => {
    getMobilePassProducts({}).then(
      (prds) => setPassProducts(prds?.content || []),
      (err) => console.error('Error fetching pass products:', err),
    );
  }, []);

  const disabledDate = useCallback(
    (current) =>
      current < moment() ||
      Date.parse(current) > Date.parse(moment().add(90, 'days')),
    [],
  );

  const groupLines = useMemo(() => {
    const routeGroupsData = routeGroups?.content ?? [];
    if (!routeGroupsData.length) return {};
    return routeGroupsData.reduce(
      (acc, val) => ({ ...acc, [val.id]: val }),
      {},
    );
  }, [routeGroups]);

  const routesMap = useMemo(
    () =>
      Object.values(groupLines)
        .flatMap((group) => group.routes ?? [])
        .reduce((acc, route) => ({ ...acc, [route.id]: route }), {}),
    [groupLines],
  );

  const lineGroupsColorMap = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(groupLines).map(([id, g]) => [
          id,
          { name: g.name, color: 'blue' },
        ]),
      ),
    [groupLines],
  );

  const routesColorMap = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(routesMap).map(([id, r]) => [
          id,
          { name: r.name, color: 'cyan' },
        ]),
      ),
    [routesMap],
  );

  const columns = useMemo(
    () => [
      {
        key: 'id',
        title: t('entity.support.mobile_orders.id'),
        dataIndex: 'id',
        width: 70,
      },
      {
        key: 'productId',
        title: t('entity.support.mobile_orders.productId'),
        dataIndex: 'productId',
        width: 70,
      },
      {
        key: 'productName',
        title: t('entity.support.mobile_orders.productName'),
        dataIndex: 'productName',
      },
      {
        key: 'productType',
        title: t('entity.support.mobile_orders.productType'),
        dataIndex: 'productType',
        width: 120,
      },
      {
        key: 'validPeriod',
        title: t('entity.support.mobile_orders.validPeriod'),
        dataIndex: 'validPeriod',
        render: (val) => val || '-',
      },
      {
        key: 'lineGroups',
        title: t('entity.support.mobile_orders.lineGroups'),
        dataIndex: 'lineGroups',
        render: (val) => renderTags(val, lineGroupsColorMap),
      },
      {
        key: 'lines',
        title: t('entity.support.mobile_orders.lines'),
        dataIndex: 'lines',
        render: (val) => renderTags(val, routesColorMap),
      },
      {
        key: 'uatStart',
        title: t('entity.support.mobile_orders.uatStart'),
        dataIndex: 'uatStart',
        render: (val) => (val ? uats[val]?.name || val : '-'),
      },
      {
        key: 'uatStop',
        title: t('entity.support.mobile_orders.uatStop'),
        dataIndex: 'uatStop',
        render: (val) => (val ? uats[val]?.name || val : '-'),
      },
      {
        key: 'quantity',
        title: t('entity.support.mobile_orders.quantity'),
        dataIndex: 'quantity',
        width: 80,
        render: (val) => (val !== undefined && val !== null ? val : '-'),
      },
      {
        key: 'deleted',
        title: t('entity.support.mobile_orders.deleted'),
        dataIndex: 'deleted',
        width: 90,
        render: (val) =>
          badge(
            val,
            'Inactiv',
            'Activat',
            '#fff1f0',
            '#e6ffed',
            '#cf1322',
            '#389e0d',
          ),
      },
      {
        key: 'active',
        title: t('entity.support.mobile_orders.active'),
        dataIndex: 'active',
        width: 80,
        render: (val) =>
          badge(val, 'Da', 'Nu', '#e6ffed', '#fff1f0', '#389e0d', '#cf1322'),
      },
    ],
    [t, lineGroupsColorMap, routesColorMap, uats],
  );

  const configureProduct = useCallback(
    (product, idx) => {
      const originalProduct = passProducts?.find(({ id }) => id === product.id);
      const existingProduct = configForm.getFieldValue('product') || {};

      configForm.setFieldsValue({
        product: {
          ...product,
          count:
            (originalProduct.category === 'PURSE'
              ? product.count / 100
              : product.count) || 1,
          startDate:
            existingProduct.startDate ||
            (product.startDate && moment(product.startDate)),
        },
        idx,
        originalProduct,
      });
      setIsModalVisible(true);
    },
    [configForm, passProducts],
  );

  const handleFetchTitles = async () => {
    try {
      const { physicalId } = form.value;

      if (!physicalId) {
        notification.error({
          message: t('errors.physicalIdMissingError'),
        });
        return;
      }

      setLoading(true);
      setHasFetched(false);
      setIsConfigured(false);
      setSelectedProduct(null);
      getMobileTitlesByPhysicalId(physicalId).then(
        (response) => {
          setUserInfo({
            fullName: response[0]?.userName ?? '-',
            userIdentifier: response[0]?.userIdentifier ?? '-',
          });
          setValue('accountId', response[0]?.accountId ?? null);
          setTitles(response || []);
          setHasFetched(true);
          setLoading(false);
        },
        (error) => {
          console.error('Error fetching titles:', error);
          setLoading(false);

          const { inner } = error || {};
          const { _: messageCode } = inner || {};

          notification.error({
            message: messageCode
              ? t(messageCode, inner)
              : t('actions.errorOccurred'),
          });
        },
      );
    } catch (error) {
      console.error('Error fetching titles:', error);
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div
          style={{
            padding: 16,
            border: '1px solid #f0f0f0',
            borderRadius: 8,
            background: '#fff',
          }}
        >
          <FormWrapper.Single>
            <div style={{ marginBottom: 6, fontWeight: 500 }}>
              {t('entity.support.mobile_orders.physical_id')}
            </div>

            <FormWrapper.Input
              props={{
                ...input('physicalId'),
                disabled: isSubmitted,
                suffix: (
                  <SearchOutlined
                    onClick={isSubmitted ? undefined : handleFetchTitles}
                    style={{
                      cursor: isSubmitted ? 'not-allowed' : 'pointer',
                      color: isSubmitted ? '#d9d9d9' : '#1677ff',
                    }}
                  />
                ),
              }}
            />
          </FormWrapper.Single>

          {userInfo && (
            <div
              style={{
                marginTop: 16,
                padding: 12,
                background: '#fafafa',
                borderRadius: 8,
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 12,
              }}
            >
              <div>
                <div style={{ fontSize: 12, color: '#888' }}>
                  {t('entity.support.mobile_orders.fullName')}
                </div>
                <div style={{ fontWeight: 600 }}>{userInfo.fullName}</div>
              </div>

              <div>
                <div style={{ fontSize: 12, color: '#888' }}>
                  {t('entity.support.mobile_orders.userIdentifier')}
                </div>
                <div style={{ fontWeight: 600 }}>{userInfo.userIdentifier}</div>
              </div>
            </div>
          )}
        </div>

        {hasFetched && (
          <div
            style={{
              padding: 16,
              border: '1px solid #f0f0f0',
              borderRadius: 8,
              background: '#fff',
            }}
          >
            <Spin spinning={loading}>
              <Table
                dataSource={titles}
                rowKey="id"
                pagination={false}
                bordered
                size="small"
                columns={columns}
                scroll={{ x: 'max-content' }}
                locale={{
                  emptyText: t('entity.support.mobile_orders.noProductsFound'),
                }}
              />
            </Spin>
          </div>
        )}

        {hasFetched && (
          <FormWrapper.Single>
            <div style={{ marginBottom: 6, fontWeight: 500 }}>
              {t('entity.support.mobile_orders.selectTitle')}
            </div>

            <div
              style={{
                display: 'flex',
                gap: 8,
                alignItems: 'center',
                flexWrap: 'wrap',
              }}
            >
              <Select
                onChange={(value) => {
                  setSelectedProduct(
                    passProducts.find((product) => product.id === value),
                  );
                }}
                allowClear
                disabled={isSubmitted}
                style={{ flex: 1, minWidth: 200 }}
                placeholder={t('entity.support.mobile_orders.selectTitle')}
                options={passProducts.map((product) => ({
                  value: product.id,
                  label: product?.name ?? product?.code,
                }))}
              />
              <Tooltip title={t('entity.orders.configureProduct')}>
                <Button
                  type="primary"
                  disabled={!selectedProduct || isSubmitted}
                  onClick={() => {
                    if (selectedProduct) {
                      configureProduct(selectedProduct, 0);
                    }
                  }}
                >
                  {t('entity.orders.configureProduct')}
                </Button>
              </Tooltip>
            </div>
          </FormWrapper.Single>
        )}

        <Modal
          title={t('entity.orders.configuration')}
          open={isModalVisible}
          onOk={() => {
            configForm
              .validateFields()
              .then((val) => {
                const idx = configForm.getFieldValue('idx');
                const { product } = val;

                const count =
                  product.count ??
                  configForm.getFieldValue(['product', 'count']) ??
                  1;

                if (selectedProduct.category === 'PURSE') {
                  if (
                    selectedProduct?.maxValue &&
                    count * 100 > selectedProduct.maxValue
                  ) {
                    return;
                  }
                  product.count = count * 100;
                } else {
                  product.count = count;
                }

                const originalProduct =
                  configForm.getFieldValue('originalProduct');
                setValue(`products[${idx}]`, {
                  ...originalProduct,
                  ...product,
                });
                setIsConfigured(true);
                setIsModalVisible(false);
              })
              .catch((info) => {
                console.error('Validate Failed:', info);
              });
          }}
          onCancel={() => {
            configForm.resetFields();
            setIsModalVisible(false);
          }}
        >
          <Form form={configForm} layout="vertical">
            <Form.Item
              noStyle
              shouldUpdate={(prev, next) =>
                prev.originalProduct?.category !==
                next.originalProduct?.category
              }
            >
              {({ getFieldValue }) => {
                const categ = getFieldValue(['originalProduct', 'category']);
                return (
                  <>
                    <Form.Item name={['originalProduct', 'name']} noStyle>
                      <Div />
                    </Form.Item>
                    {categ && (categ === 'TICKET' || categ === 'PURSE') && (
                      <Row gutter={16}>
                        <Col span={8}>
                          <POSButton
                            title={t('entity.orders.subtract')}
                            onClick={() =>
                              configForm.setFieldsValue({
                                product: {
                                  ...configForm.getFieldValue('product'),
                                  count: Math.max(
                                    1,
                                    configForm.getFieldValue([
                                      'product',
                                      'count',
                                    ]) - 1,
                                  ),
                                },
                              })
                            }
                            color="red"
                            size="xsmall"
                          />
                        </Col>
                        <Col span={8}>
                          <Form.Item
                            noStyle
                            name={['product', 'count']}
                            rules={[
                              {
                                type: 'number',
                                message: t('errors.maxValueExceeded'),
                              },
                            ]}
                          >
                            {categ === 'PURSE' ? (
                              <InputNumber
                                className="account-custom-input-number"
                                min={1}
                                max={
                                  getFieldValue(['originalProduct', 'maxValue'])
                                    ? getFieldValue([
                                        'originalProduct',
                                        'maxValue',
                                      ]) / 100
                                    : undefined
                                } // Conditionally add the upper bound
                                defaultValue={1}
                                step={0.01}
                                addonAfter="RON"
                                parser={(purseVal) =>
                                  purseVal.replace(',', '.')
                                }
                              />
                            ) : (
                              <Input
                                style={{ height: '100%', fontSize: '2rem' }}
                                disabled
                              />
                            )}
                          </Form.Item>
                        </Col>
                        <Col span={8}>
                          <POSButton
                            title={t('entity.orders.add')}
                            onClick={() =>
                              configForm.setFieldsValue({
                                product: {
                                  ...configForm.getFieldValue('product'),
                                  count:
                                    configForm.getFieldValue([
                                      'product',
                                      'count',
                                    ]) + 1,
                                },
                              })
                            }
                            color="green"
                            size="xsmall"
                          />
                        </Col>
                      </Row>
                    )}
                  </>
                );
              }}
            </Form.Item>

            <Divider dashed />

            <Form.Item
              noStyle
              shouldUpdate={(prev, next) => {
                return prev?.product?.productId !== next?.product?.productId;
              }}
            >
              {({ getFieldValue }) => {
                return (
                  getFieldValue(['originalProduct', 'validityStartType']) ===
                    'FUTURE' && (
                    <Form.Item
                      name={['product', 'startDate']}
                      label={t('entity.orders.startDateSubscription')}
                      rules={[
                        {
                          required: true,
                          message: t('errors.selectDate'),
                        },
                      ]}
                    >
                      <DatePickerLocale
                        format="DD.MM.YYYY HH:mm"
                        showTime={{ defaultValue: moment('00:00:00', 'HH:mm') }}
                        style={{ width: '100%' }}
                        disabledDate={disabledDate}
                      />
                    </Form.Item>
                  )
                );
              }}
            </Form.Item>

            <Form.Item
              noStyle
              shouldUpdate={(prev, next) => {
                return prev?.originalProduct?.id !== next?.originalProduct?.id;
              }}
            >
              {({ getFieldValue }) => {
                const restrictions = getFieldValue([
                  'originalProduct',
                  'productRestrictions',
                ])?.filter(({ restriction }) =>
                  ['LINE_GROUPS', 'ZONAL'].includes(restriction),
                );
                const category = getFieldValue(['originalProduct', 'category']);

                if (!restrictions || restrictions.length === 0) return <div />;

                return (
                  <>
                    {restrictions.map((restrictionItem) => {
                      switch (restrictionItem.restriction) {
                        case 'LINE_GROUPS':
                          return (
                            <Restriction
                              key={restrictionItem.id ?? 'line-groups'}
                              form={configForm}
                              restrictions={restrictionItem}
                            />
                          );

                        case 'ZONAL':
                          return (
                            metropolitanEnabled &&
                            category === 'PASS' && (
                              <ZonalRestriction
                                key={restrictionItem.id ?? 'zonal'}
                                form={configForm}
                                axisData={axis}
                                restrictions={restrictionItem}
                              />
                            )
                          );
                        default:
                          return <div />;
                      }
                    })}
                  </>
                );
              }}
            </Form.Item>
          </Form>
        </Modal>

        {isConfigured && (
          <Space>
            <Button
              type="primary"
              disabled={!form.isValid || isSubmitted}
              onClick={() => setIsConfirmModalVisible(true)}
            >
              {t('actions.save')}
            </Button>
            <CancelButton onCancel={onCancel} />
          </Space>
        )}

        <Modal
          open={isConfirmModalVisible}
          onOk={() => {
            setIsConfirmModalVisible(false);
            handleSubmit();
          }}
          onCancel={() => setIsConfirmModalVisible(false)}
          okText={t('actions.confirm')}
          cancelText={t('entity.pos.actions.cancel')}
        >
          <p>
            {t('entity.support.mobile_orders.confirmAddProduct', {
              name: userInfo?.fullName,
            })}
          </p>
        </Modal>
      </div>
    </form>
  );
};

export default SuportMobileOrderForm;
